import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { CaretDown, CaretRight, Plus, PencilSimple, Trash, Lightning, Pencil as PencilManual, ChartBar, Briefcase, Notebook, FileText, Eye, DownloadSimple, GraduationCap } from "@phosphor-icons/react";
import { toast } from "sonner";
import EmployeeCertifications from "@/components/EmployeeCertifications";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_LABELS = {
  formazione: "Formazione", coaching: "Coaching", mentoring: "Mentoring",
  retention: "Retention", altro: "Altro",
};
const CATEGORY_ORDER = ["formazione", "coaching", "mentoring", "retention", "altro"];
const STATUS_FILTERS = [
  { v: "all", label: "Tutti" },
  { v: "da_approvare", label: "Da approvare" },
  { v: "da_fare", label: "Da fare" },
  { v: "in_corso", label: "In corso" },
  { v: "completato", label: "Completati" },
];
const STATUS_ICON = { da_approvare: "⏳", da_fare: "☐", in_corso: "◐", completato: "☑" };
// FEATURE-03: cycle solo per task approvati (☐ → ◐ → ☑ → ☐). I `da_approvare` non ciclano (richiede approve).
const STATUS_NEXT = { da_fare: "in_corso", in_corso: "completato", completato: "da_fare" };
const STATUS_LABEL = { da_approvare: "Da approvare", da_fare: "Da fare", in_corso: "In corso", completato: "Completato" };

export default function SchedaCard({ emp, expanded, onToggle, onChanged }) {
  const [plan, setPlan] = useState({ items: [], grouped: {}, counts: { da_approvare: 0, da_fare: 0, in_corso: 0, completato: 0 }, total: 0 });
  const [statusFilter, setStatusFilter] = useState("all");
  const [addOpen, setAddOpen] = useState(false);
  const [editTask, setEditTask] = useState(null); // task object or null
  const [form, setForm] = useState({ title: "", category: "formazione", due_date: "", no_due_date: false, description: "" });
  // BUG-63: stato locale per loader generazione percorso + counter elapsed
  const [generatingPlan, setGeneratingPlan] = useState(false);
  const [genElapsed, setGenElapsed] = useState(0);
  useEffect(() => {
    if (!generatingPlan) { setGenElapsed(0); return undefined; }
    const t = setInterval(() => setGenElapsed(x => x + 1), 1000);
    return () => clearInterval(t);
  }, [generatingPlan]);

  const reload = useCallback(async () => {
    if (!emp?.id) return;
    try {
      const { data } = await axios.get(`${API}/employees/${emp.id}/action-plan`, { withCredentials: true });
      setPlan(data);
      onChanged && onChanged();
    } catch { /* noop */ }
  }, [emp?.id, onChanged]);

  useEffect(() => { if (expanded) reload(); }, [expanded, reload]);

  const cycleStatus = async (task) => {
    // FEATURE-03: i task `da_approvare` non ciclano (serve Approva)
    if (task.status === "da_approvare") {
      toast.error("Approva il task prima di cambiarne lo stato");
      return;
    }
    const next = STATUS_NEXT[task.status || "da_fare"];
    try {
      await axios.patch(`${API}/action-plan/${task.id}`, { status: next }, { withCredentials: true });
      toast.success(`Stato → ${STATUS_LABEL[next]}`);
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };

  const approveTask = async (task) => {
    try {
      await axios.post(`${API}/action-plan/${task.id}/approve`, {}, { withCredentials: true });
      toast.success("Task approvato");
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };

  const approveAll = async () => {
    if (!plan.counts.da_approvare) return;
    if (!window.confirm(`Approvare tutti i ${plan.counts.da_approvare} task in attesa?`)) return;
    try {
      const { data } = await axios.post(`${API}/employees/${emp.id}/action-plan/approve-all`, {}, { withCredentials: true });
      toast.success(`Approvati ${data.approved} task`);
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };

  const generatePath = async () => {
    if (generatingPlan) return; // BUG-63: early-out su doppio-click rapido
    if (emp.test_status !== "Completato") {
      toast.error("Completa prima l'analisi comportamentale");
      return;
    }
    const hasSystemTasks = plan.items.some(t => t.source === "system");
    if (hasSystemTasks && !window.confirm("I task non approvati saranno sostituiti. Continuare?")) return;
    setGeneratingPlan(true);
    try {
      const { data } = await axios.post(`${API}/employees/${emp.id}/generate-action-plan`, {}, { withCredentials: true });
      toast.success(`Percorso generato — ${data.count} task in attesa di approvazione`);
      reload();
    } catch (e) {
      const status = e.response?.status;
      if (status === 409) {
        toast.error("Generazione già in corso, attendi qualche secondo");
      } else {
        toast.error(e.response?.data?.detail || "Errore durante la generazione, riprova");
      }
    } finally {
      setGeneratingPlan(false);
    }
  };

  const submitTask = async () => {
    if (!form.title.trim()) { toast.error("Titolo richiesto"); return; }
    const body = {
      title: form.title.trim(), category: form.category,
      description: form.description?.trim() || null,
      due_date: form.no_due_date ? null : (form.due_date || null),
    };
    try {
      if (editTask) {
        await axios.patch(`${API}/action-plan/${editTask.id}`, body, { withCredentials: true });
        toast.success("Task aggiornato");
      } else {
        await axios.post(`${API}/employees/${emp.id}/action-plan`, body, { withCredentials: true });
        toast.success("Task aggiunto");
      }
      setAddOpen(false); setEditTask(null);
      setForm({ title: "", category: "formazione", due_date: "", no_due_date: false, description: "" });
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };

  const deleteTask = async (task) => {
    if (!window.confirm(`Spostare "${task.title}" nel cestino?\nPotrai ripristinarlo entro 30 giorni.`)) return;
    try {
      await axios.delete(`${API}/action-plan/${task.id}`, { withCredentials: true });
      toast.success("Task spostato nel cestino");
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };

  const openEdit = (task) => {
    setEditTask(task);
    setForm({
      title: task.title || "", category: task.category || "altro",
      due_date: task.due_date || "", no_due_date: !task.due_date,
      description: task.description || "",
    });
    setAddOpen(true);
  };

  const filteredItems = plan.items.filter(it =>
    statusFilter === "all" ? true : (it.status === statusFilter)
  );
  const groupedFiltered = CATEGORY_ORDER.reduce((acc, cat) => {
    const items = filteredItems.filter(i => (i.category || "altro") === cat);
    if (items.length) acc[cat] = items;
    return acc;
  }, {});

  const initials = `${(emp.first_name || "?")[0]}${(emp.last_name || "")[0] || ""}`.toUpperCase();
  const profile = emp.scores?.profile_label || (emp.test_status === "Completato" ? "—" : "Non disponibile");
  const total = emp.scores?.total_score;

  return (
    <div className="border border-[#0D1F3C]/8 rounded-lg bg-white" data-testid={`scheda-${emp.id}`}>
      {/* Riga compatta */}
      <button
        onClick={() => onToggle(expanded ? null : emp.id)}
        className="w-full flex items-center gap-4 px-5 py-4 hover:bg-[#1B4FD8]/5 transition-colors text-left"
        data-testid={`scheda-toggle-${emp.id}`}
      >
        <div className="w-10 h-10 rounded-full bg-[#1B4FD8] text-white flex items-center justify-center text-sm font-semibold">{initials}</div>
        <div className="flex-1 min-w-0">
          <div className="font-medium text-[#0D1F3C]">{emp.first_name} {emp.last_name}</div>
          <div className="text-xs text-[#0D1F3C]/50">{emp.job_title || "—"} · {emp.department || "—"}</div>
        </div>
        <Badge variant="outline" className={`text-[11px] ${emp.test_status === "Completato" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-gray-50 text-gray-500"}`}>
          {emp.test_status === "Completato" ? `🟢 Analisi · ${total ?? "—"}` : "⚪ Analisi non completata"}
        </Badge>
        <Badge variant="outline"
          className={`text-[11px] ${(plan.total || emp.plan_task_count || 0) > 0 ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-gray-50 text-gray-500"}`}
          title={emp.plan_status_breakdown ? `${emp.plan_task_count || 0} task totali · ${emp.plan_status_breakdown.da_approvare || 0} da approvare · ${emp.plan_status_breakdown.da_fare || 0} da fare · ${emp.plan_status_breakdown.in_corso || 0} in corso · ${emp.plan_status_breakdown.completato || 0} completati` : ""}
          data-testid={`scheda-plan-chip-${emp.id}`}
        >
          ✅ Piano · {plan.total || emp.plan_task_count || 0} task
        </Badge>
        {expanded ? <CaretDown size={18} /> : <CaretRight size={18} />}
      </button>

      {/* Card espansa */}
      {expanded && (
        <div className="border-t border-[#0D1F3C]/8 px-5 py-5 space-y-6 bg-gradient-to-b from-white to-[#1B4FD8]/2">

          {/* INFO */}
          <section data-testid={`scheda-info-${emp.id}`}>
            <h4 className="text-xs uppercase tracking-wide text-[#0D1F3C]/50 mb-3 flex items-center gap-2">
              <Briefcase size={14} /> Info
            </h4>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
              <div><span className="text-[#0D1F3C]/50">Email:</span> {emp.email || "—"}</div>
              <div data-testid={`scheda-phone-${emp.id}`}><span className="text-[#0D1F3C]/50">Telefono:</span> {emp.phone || "—"}</div>
              <div><span className="text-[#0D1F3C]/50">Ruolo:</span> {emp.job_title || "—"}</div>
              <div><span className="text-[#0D1F3C]/50">Reparto:</span> {emp.department || "—"}</div>
              <div><span className="text-[#0D1F3C]/50">Anzianità:</span> {emp.years_experience || 0} anni</div>
              <div><span className="text-[#0D1F3C]/50">Tipo:</span> {emp.is_manager ? "Responsabile" : "Dipendente"}</div>
              <div><span className="text-[#0D1F3C]/50">Manager:</span> {emp.manager_name || "—"}</div>
            </div>
          </section>

          {/* ANALISI COMPORTAMENTALE */}
          <section data-testid={`scheda-analisi-${emp.id}`}>
            <h4 className="text-xs uppercase tracking-wide text-[#0D1F3C]/50 mb-3 flex items-center gap-2">
              <ChartBar size={14} /> Analisi Comportamentale
            </h4>
            {/* BUG-61: stesso source-of-truth della colonna ANALISI in Lista (solo test_status) */}
            {emp.test_status === "Completato" ? (
              <div className="flex flex-wrap items-center gap-3 text-sm">
                <Badge className="bg-[#1B4FD8] text-white text-sm px-3 py-1">{total != null ? `${total} ${profile}` : "Completata"}</Badge>
                {emp.test_completed_at && <span className="text-xs text-[#0D1F3C]/50">Completata il {new Date(emp.test_completed_at).toLocaleDateString('it-IT')}</span>}
                <Button size="sm" variant="outline" onClick={() => onToggle('open-report:' + emp.id)} data-testid={`scheda-open-report-${emp.id}`}><Eye size={14} className="mr-1" /> Apri report</Button>
                <Button size="sm" variant="outline" onClick={async () => {
                  try {
                    const { data: rep } = await axios.get(`${API}/employees/${emp.id}/report-status`, { withCredentials: true });
                    if (!rep.report_id) { toast.error("Report non disponibile"); return; }
                    const r = await axios.get(`${API}/reports/${rep.report_id}/pdf`, { withCredentials: true, responseType: 'blob' });
                    const url = window.URL.createObjectURL(new Blob([r.data]));
                    const a = document.createElement('a'); a.href = url; a.download = `report_${emp.first_name}_${emp.last_name}.pdf`;
                    document.body.appendChild(a); a.click(); a.remove();
                  } catch { toast.error("Errore PDF"); }
                }}><DownloadSimple size={14} className="mr-1" /> PDF</Button>
              </div>
            ) : (
              <p className="text-sm text-[#0D1F3C]/50">Analisi non ancora completata.</p>
            )}
          </section>

          {/* ROADMAP DI SVILUPPO */}
          <section data-testid={`scheda-roadmap-${emp.id}`}>
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
              <h4 className="text-xs uppercase tracking-wide text-[#0D1F3C]/50 flex items-center gap-2 flex-wrap">
                <GraduationCap size={14} /> Roadmap di Sviluppo
                <span className="text-[#0D1F3C]/40">·</span>
                <span className="text-[#0D1F3C]/70">{plan.total} task</span>
                <span className="text-[#0D1F3C]/40">·</span>
                <span className="text-emerald-700">{plan.counts.completato} completati</span>
                <span className="text-[#0D1F3C]/40">·</span>
                <span className="text-amber-700">{plan.counts.in_corso} in corso</span>
                {plan.counts.da_approvare > 0 && (
                  <>
                    <span className="text-[#0D1F3C]/40">·</span>
                    <span className="text-[#1B4FD8]">{plan.counts.da_approvare} da approvare</span>
                  </>
                )}
              </h4>
              <div className="flex items-center gap-2 flex-wrap">
                {STATUS_FILTERS.map(f => {
                  const count = f.v === "all" ? plan.total : (plan.counts[f.v] || 0);
                  return (
                    <button key={f.v} onClick={() => setStatusFilter(f.v)}
                      className={`px-2 py-1 rounded text-[11px] transition ${statusFilter === f.v ? "bg-[#1B4FD8] text-white" : "bg-[#0D1F3C]/5 text-[#0D1F3C]/70 hover:bg-[#0D1F3C]/10"}`}
                      data-testid={`scheda-filter-${f.v}`}>
                      {f.label} {count > 0 && <span className="opacity-70">{count}</span>}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Bulk approve banner */}
            {plan.counts.da_approvare > 0 && (
              <div className="flex items-center justify-between gap-3 px-4 py-2.5 mb-3 rounded-md border border-[#1B4FD8]/30 bg-[#1B4FD8]/5"
                   data-testid={`scheda-bulk-approve-banner-${emp.id}`}>
                <span className="text-sm text-[#0D1F3C]/80">
                  ⏳ <strong>{plan.counts.da_approvare}</strong> task in attesa di approvazione
                </span>
                <Button size="sm" onClick={approveAll} className="bg-[#1B4FD8] hover:bg-[#0D1F3C] text-white" data-testid={`scheda-approve-all-${emp.id}`}>
                  Approva tutti
                </Button>
              </div>
            )}

            {plan.total === 0 ? (
              <p className="text-sm text-[#0D1F3C]/50 mb-3">Nessuna attività di sviluppo. Genera il percorso dall'analisi o aggiungine una manuale.</p>
            ) : (
              <div className="space-y-4">
                {Object.entries(groupedFiltered).map(([cat, items]) => (
                  <div key={cat} data-testid={`scheda-cat-${cat}-${emp.id}`}>
                    <div className="text-[11px] uppercase font-medium text-[#1B4FD8]/80 mb-2">{CATEGORY_LABELS[cat]}</div>
                    <ul className="space-y-1.5">
                      {items.map(t => {
                        const isPendingApproval = t.status === "da_approvare";
                        return (
                          <li key={t.id} className="flex items-start gap-3 px-3 py-2 rounded-md border border-[#0D1F3C]/8 hover:border-[#1B4FD8]/30 bg-white" data-testid={`scheda-task-${t.id}`}>
                            <button onClick={() => cycleStatus(t)} className="text-lg leading-none w-6 hover:scale-110 transition-transform mt-0.5 flex-shrink-0"
                              title={STATUS_LABEL[t.status || "da_fare"]}
                              data-testid={`scheda-task-status-${t.id}`}>
                              {STATUS_ICON[t.status || "da_fare"]}
                            </button>
                            {/* BUG-62: word-wrap, no ellipsis, multilinea pulita */}
                            <span className={`flex-1 text-sm break-words whitespace-normal ${t.status === "completato" ? "line-through text-[#0D1F3C]/40" : "text-[#0D1F3C]"}`}>{t.title}</span>
                            <span title={t.source === "system" ? "Generato da Selly" : "Manuale"} className="text-[#0D1F3C]/40 flex-shrink-0 mt-0.5">
                              {t.source === "system" ? <Lightning size={14} weight="fill" className="text-[#1B4FD8]" /> : <PencilManual size={14} />}
                            </span>
                            <span className="text-xs text-[#0D1F3C]/50 w-24 text-right flex-shrink-0 mt-0.5">
                              {t.due_date ? new Date(t.due_date).toLocaleDateString('it-IT') : "no scadenza"}
                            </span>
                            {/* FEATURE-03: bottoni Approva/Rifiuta inline solo per da_approvare */}
                            {isPendingApproval ? (
                              <div className="flex items-center gap-1 flex-shrink-0">
                                <Button size="sm" onClick={() => approveTask(t)} className="h-7 bg-emerald-600 hover:bg-emerald-700 text-white text-xs px-2"
                                  data-testid={`scheda-task-approve-${t.id}`}>Approva</Button>
                                <Button size="sm" variant="ghost" onClick={() => deleteTask(t)} className="h-7 text-[#0D1F3C]/60 text-xs px-2"
                                  data-testid={`scheda-task-reject-${t.id}`}>Rifiuta</Button>
                              </div>
                            ) : (
                              <>
                                <button onClick={() => openEdit(t)} className="text-[#0D1F3C]/50 hover:text-[#1B4FD8] flex-shrink-0 mt-0.5" title="Modifica" data-testid={`scheda-task-edit-${t.id}`}><PencilSimple size={14} /></button>
                                <button onClick={() => deleteTask(t)} className="text-red-500/70 hover:text-red-600 flex-shrink-0 mt-0.5" title="Sposta nel cestino" data-testid={`scheda-task-delete-${t.id}`}><Trash size={14} /></button>
                              </>
                            )}
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                ))}
              </div>
            )}

            <div className="mt-4 flex items-center gap-2 flex-wrap">
              <Button size="sm" variant="outline" onClick={() => { setEditTask(null); setForm({ title: "", category: "formazione", due_date: "", no_due_date: false, description: "" }); setAddOpen(true); }}
                className="border-[#1B4FD8]/30 text-[#1B4FD8]" data-testid={`scheda-add-task-${emp.id}`}>
                <Plus size={14} className="mr-1" /> Aggiungi attività manuale
              </Button>
              {/* REFACTOR B: bottone "Genera percorso" qui, non più dentro il report */}
              <Button size="sm" variant="outline"
                onClick={generatePath}
                disabled={emp.test_status !== "Completato" || generatingPlan}
                title={emp.test_status !== "Completato" ? "Completa prima l'analisi comportamentale" : ""}
                className={`border-[#1B4FD8]/30 text-[#1B4FD8] ${(emp.test_status !== "Completato" || generatingPlan) ? "opacity-60 cursor-not-allowed" : ""}`}
                data-testid={`scheda-generate-path-${emp.id}`}>
                {generatingPlan ? (
                  <>
                    <span className="inline-block w-3.5 h-3.5 border-2 border-[#1B4FD8] border-t-transparent rounded-full animate-spin mr-2" />
                    Generazione in corso… {genElapsed}s
                  </>
                ) : (
                  <><Lightning size={14} className="mr-1" weight="fill" /> {plan.items.some(t => t.source === "system") ? "Rigenera percorso" : "Genera percorso da analisi"}</>
                )}
              </Button>
            </div>
          </section>

          {/* NOTE HR */}
          <section data-testid={`scheda-notes-${emp.id}`}>
            <h4 className="text-xs uppercase tracking-wide text-[#0D1F3C]/50 mb-2 flex items-center gap-2">
              <Notebook size={14} /> Note HR
            </h4>
            <p className="text-sm text-[#0D1F3C]/70 whitespace-pre-wrap">{emp.hr_notes || <span className="italic text-[#0D1F3C]/30">Nessuna nota</span>}</p>
          </section>

          {/* CERTIFICAZIONI — BUG-70: usa shared component (source-of-truth identico al modale Lista) */}
          <section data-testid={`scheda-certs-${emp.id}`}>
            <EmployeeCertifications employeeId={emp.id} />
          </section>
        </div>
      )}

      {/* Dialog aggiungi/modifica task */}
      <Dialog open={addOpen} onOpenChange={(o) => { if (!o) { setAddOpen(false); setEditTask(null); } }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editTask ? "Modifica attività" : "Nuova attività manuale"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Titolo</Label>
              <Input value={form.title} maxLength={250} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} placeholder="Es: Affianca Marina sul nuovo CRM" data-testid="task-form-title" />
            </div>
            <div>
              <Label className="text-xs">Categoria</Label>
              <Select value={form.category} onValueChange={v => setForm(f => ({ ...f, category: v }))}>
                <SelectTrigger data-testid="task-form-category"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CATEGORY_ORDER.map(c => <SelectItem key={c} value={c}>{CATEGORY_LABELS[c]}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Scadenza</Label>
              <div className="flex items-center gap-2">
                <Input type="date" value={form.due_date} disabled={form.no_due_date}
                  onChange={e => setForm(f => ({ ...f, due_date: e.target.value }))} data-testid="task-form-due" />
                <label className="flex items-center gap-1 text-xs text-[#0D1F3C]/70 whitespace-nowrap">
                  <input type="checkbox" checked={form.no_due_date} onChange={e => setForm(f => ({ ...f, no_due_date: e.target.checked, due_date: e.target.checked ? "" : f.due_date }))} />
                  Nessuna scadenza
                </label>
              </div>
            </div>
            <div>
              <Label className="text-xs">Note (opzionale)</Label>
              <Textarea rows={2} maxLength={500} value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} data-testid="task-form-desc" />
            </div>
            <Button onClick={submitTask} className="w-full bg-[#1B4FD8] hover:bg-[#0D1F3C]" data-testid="task-form-save">
              {editTask ? "Salva modifiche" : "Aggiungi attività"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
